// Sentry initialization should be imported first!
import "./instrument";
import "./i18n";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import "./index.css";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { darkTheme } from "./theme/darkTheme";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { routeTree } from "./routeTree.gen";
import { ApiError } from "./types/Api";
import { SnackbarProvider } from "notistack";
import { MotionConfig } from "framer-motion";
import { client } from "./client/client.gen";
import { API_URL } from "./api/apiClient";
import { NotFoundPage } from "./components/shared/NotFoundPage";

// Configure the generated OpenAPI client (heyapi) with baseUrl and auth
client.setConfig({
  baseUrl: API_URL || undefined,
  auth: () => localStorage.getItem("access_token") || undefined,
});

const handleApiError = (error: Error) => {
  console.log("handleApiError", error);
  if (error instanceof ApiError) {
    if (error.status === 401) {
      // Store the reason for logout
      const errorBody = error.body as { detail?: string } | string;
      const errorDetail = typeof errorBody === 'object' && errorBody?.detail
        ? errorBody.detail
        : typeof errorBody === 'string'
        ? errorBody
        : "Authentication required";

      if (errorDetail.toLowerCase().includes("expired")) {
        sessionStorage.setItem("authError", "Your session has expired. Please sign in again.");
      } else if (errorDetail.toLowerCase().includes("not authenticated")) {
        sessionStorage.setItem("authError", "Please sign in to continue.");
      } else {
        sessionStorage.setItem("authError", "Authentication failed. Please sign in again.");
      }

      console.log("Invalid token, redirecting to login:", errorDetail);
      localStorage.removeItem("access_token");
      window.location.href = "/login";
    }
  }
};
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on any HTTP errors (ApiError has status code)
        // Only retry on network errors (no status code - connection issues, timeouts, etc.)
        if (error instanceof ApiError) {
          return false;
        }
        // Retry network errors up to 2 times
        return failureCount < 2;
      },
    },
    mutations: {
      retry: (_failureCount, error) => {
        // Don't retry mutations on authentication errors
        if (error instanceof ApiError && [401, 403].includes(error.status)) {
          return false;
        }
        // Don't retry mutations by default
        return false;
      },
    },
  },
  queryCache: new QueryCache({
    onError: handleApiError,
  }),
  mutationCache: new MutationCache({
    onError: handleApiError,
  }),
});

// Use the dark theme by default
const theme = darkTheme;

// Default error component for lazy route chunk load failures
function RouteErrorFallback({ error }: { error: Error }) {
  const isChunkError =
    error.message?.includes("Failed to fetch dynamically imported module") ||
    error.message?.includes("Importing a module script failed") ||
    error.message?.includes("Loading chunk") ||
    error.message?.includes("ChunkLoadError");

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200, gap: 12 }}>
      <p style={{ color: "#ef4444", margin: 0 }}>
        {isChunkError ? "Failed to load page. Please check your connection." : "Something went wrong."}
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid #3b82f6", background: "transparent", color: "#3b82f6", cursor: "pointer" }}
      >
        Reload
      </button>
    </div>
  );
}

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultErrorComponent: RouteErrorFallback,
  // Branded 404 + legacy /subscribe → /billing redirect (CC-PB-2 REV-6)
  defaultNotFoundComponent: NotFoundPage,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <MotionConfig reducedMotion="user">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <QueryClientProvider client={queryClient}>
              <SnackbarProvider>
                <RouterProvider router={router} />
              </SnackbarProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </LocalizationProvider>
      </MotionConfig>
    </StrictMode>
  );
}
